import { deployApp } from '@wix/ambassador-velo-apps-v1-app/http';
import {
  AppType,
  DeploymentOperation,
  RcLabel,
} from '@wix/ambassador-velo-apps-v1-app/types';
import type { AuthState } from '@wix/cli-auth';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getDeployment } from '@wix/ambassador-document-management-editor-deployments-v1-deployment/http';
import { DeploymentPipelinesStatus } from '@wix/ambassador-document-management-editor-deployments-v1-deployment/types';
import type { VariantOf, TypesOf } from 'variant';
import { fields, match, variant } from 'variant';
import pWaitFor from 'p-wait-for';
import type { ProjectModel } from '../model.js';
import { httpRequest } from '../http-request.js';
import { loadProjectGridAppContent } from './loadProjectGridAppContent.js';
import {
  deployAppResponseSchema,
  getDeploymentResponseSchema,
} from '../schemas.js';

export const DeploymentSource = variant({
  local: {},
  remote: fields<{
    branch?: string;
  }>(),
});
export type DeploymentSource = VariantOf<typeof DeploymentSource>;
export type DeploymentSourceTypes = TypesOf<typeof DeploymentSource>;

export async function getDeploymentSourceData(
  model: ProjectModel,
  source: DeploymentSource
) {
  return match(source, {
    local: async () => ({
      deploymentData: {
        revision: model.revision,
        content: await loadProjectGridAppContent(model.projectFolder),
      },
    }),
    remote: () => ({}),
  });
}

export async function deployGridApp(opts: {
  model: ProjectModel;
  operation: DeploymentOperation;
  source: DeploymentSource;
  authState: AuthState;
}) {
  const deploymentSourceData = await getDeploymentSourceData(
    opts.model,
    opts.source
  );

  try {
    const { data } = await httpRequest(
      {
        type: 'code',
        authState: opts.authState,
      },
      deployApp({
        deploymentOperation: opts.operation,
        appType: AppType.VELO_ISOLATED,
        ...deploymentSourceData,
        ...(opts.operation === DeploymentOperation.RC && {
          rcDeploymentParams: {
            rcLabel: RcLabel.WIX_CLI,
          },
        }),
      }),
      { retries: 3, maxTimeout: 3 * 1000 }
    );

    const result = deployAppResponseSchema.safeParse(data);

    if (!result.success) {
      throw new CliError({
        code: CliErrorCode.InvalidDeployAppResponse(),
        info: { data },
        cause: result.error,
      });
    }

    return result.data.deploymentInfo;
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToDeploySite(),
      cause: e,
    });
  }
}

export async function getDeploymentStatus({
  deploymentId,
  authState,
}: {
  deploymentId: string;
  authState: AuthState;
}) {
  const deploymentStatus = await pWaitFor(
    async () => {
      try {
        const { data } = await httpRequest(
          {
            type: 'editor',
            authState,
          },
          getDeployment({
            deploymentId,
          }),
          { retries: 2 }
        );

        const result = getDeploymentResponseSchema.safeParse(data);

        if (!result.success) {
          throw new CliError({
            code: CliErrorCode.InvalidGetDeploymentResponse(),
            info: { data },
            cause: result.error,
          });
        }

        const status = result.data.deployment.deploymentPipelinesStatus;

        // Continue polling
        if (status === DeploymentPipelinesStatus.IN_PROGRESS) {
          return false;
        }

        return pWaitFor.resolveWith(status);
      } catch (err) {
        throw new CliError({
          code: CliErrorCode.FailedToGetDeploymentStatus(),
          cause: err,
        });
      }
    },
    {
      // Poll each 2 seconds
      interval: 2 * 1000,
      // Fail if 2 minutes passed
      timeout: 2 * 60 * 1000,
    }
  );

  return deploymentStatus;
}
